import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Download, Phone, ShieldCheck, Building2, TrendingUp, Plus } from "lucide-react";
import { BRAND, IMAGES, SERVICES, WHY_US, CHALLENGES, FAQ } from "../lib/data";
import { brochureUrl } from "../lib/api";
import LeadForm from "../components/LeadForm";
import { LoanPortfolio } from "../components/LoanPortfolio";
import { CoreServiceCard } from "../components/CoreServiceCard";
import { PartnerTicker } from "../components/PartnerTicker";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[600px] md:min-h-[680px] py-20 md:py-0 overflow-hidden" data-testid="hero-section">
        <img src={IMAGES.hero} alt="Corporate handshake with city skyline" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative container-x md:h-[680px] flex items-center">
          <div className="max-w-3xl text-white">
            <span className="inline-flex items-center gap-2 bg-brandred text-white text-[11px] font-bold tracking-[0.2em] uppercase px-3 py-1.5 rounded-sm mb-6" data-testid="hero-badge">
              <ShieldCheck size={14} /> Pan-India Funding Specialists
            </span>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.1] tracking-tight">
              Your Strategic Partner for <span className="text-brandred">High-Ticket Funding</span> & Complex Financial Solutions.
            </h1>
            <p className="mt-5 md:mt-6 text-sm md:text-lg text-white/85 max-w-2xl leading-relaxed">
              Welcome to Crew Connect FinInvesto. We bridge the gap between ambitious businesses and the capital you need — from Nationalized Banks and NBFCs to Private Funding for high-ticket and challenging cases.
            </p>
            <div className="mt-7 md:mt-8 flex flex-wrap gap-3">
              <Link to="/contact#lead" className="bg-brandred hover:bg-brandred-700 text-white px-5 sm:px-7 py-3 sm:py-3.5 text-xs sm:text-sm font-bold tracking-wider uppercase rounded-sm flex items-center gap-2 transition-colors" data-testid="hero-apply">
                Apply for Funding <ArrowRight size={16} />
              </Link>
              <a href={brochureUrl} target="_blank" rel="noopener noreferrer" className="bg-white/10 hover:bg-white/20 border border-white/30 text-white px-5 sm:px-7 py-3 sm:py-3.5 text-xs sm:text-sm font-bold tracking-wider uppercase rounded-sm flex items-center gap-2 transition-colors" data-testid="hero-brochure">
                <Download size={16} /> Brochure
              </a>
              <a href={`tel:${BRAND.phone}`} className="text-white px-2 py-3 sm:py-3.5 text-xs sm:text-sm font-semibold flex items-center gap-2 hover:text-brandred transition-colors" data-testid="hero-phone">
                <Phone size={16} /> {BRAND.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CHALLENGES STRIP */}
      <section className="bg-navy text-white" data-testid="challenges-section">
        <div className="container-x py-12">
          <p className="text-xs uppercase tracking-[0.3em] text-brandred mb-3">We specialize in challenging cases</p>
          <h2 className="font-heading text-2xl md:text-3xl font-bold mb-8">Got a Rejected File? We Likely Have a Solution.</h2>
          <div className="grid md:grid-cols-3 gap-x-8 gap-y-3">
            {CHALLENGES.map((c, index) => (
              <div key={c} className="flex items-start gap-3 text-sm text-white/90" data-testid={`challenge-${index}`}>
                <CheckCircle2 size={18} className="mt-0.5 text-brandred shrink-0" />
                <span>{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PartnerTicker />

      {/* STATS */}
      <section className="container-x pt-20 md:pt-24 pb-6 md:pb-8" data-testid="stats-section">
        <div className="stats-grid">
          {[
            { num: "₹500Cr+", label: "Funded across Pan-India" },
            { num: "100+", label: "Lender Partner Network" },
            { num: "30 Days", label: "Avg. Project Closure" },
            { num: "24 Hrs", label: "Lead Response Time" },
          ].map((s, index) => (
            <div key={s.label} className="stat-item" data-testid={`stat-${index}`}>
              <div className="stat-num text-4xl lg:text-5xl text-navy" data-testid={`stat-number-${index}`}>{s.num}</div>
              <div className="text-sm text-muted-foreground mt-2" data-testid={`stat-label-${index}`}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-secondary section-pad mt-16 md:mt-20" data-testid="home-services">
        <div className="container-x">
          <div className="max-w-3xl mb-12">
            <span className="accent-bar mb-3" />
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy">Our Core Services</h2>
            <p className="text-muted-foreground mt-3">Tailored liquidity solutions for Mid-Corporate Industries, Traders and Builders across India.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {SERVICES.map((s) => (
              <CoreServiceCard key={s.id} service={s} />
            ))}
          </div>
          <div className="mt-10">
            <Link to="/services" className="inline-flex items-center gap-2 font-semibold text-brandred hover:text-brandred-700" data-testid="see-all-services">
              See full service breakdown <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* LOAN PRODUCTS */}
      <section className="portfolio-section section-pad" data-testid="loan-products">
        <div className="container-x">
          <div className="portfolio-heading">
            <div className="max-w-2xl">
              <span className="accent-bar mb-3" />
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy">Loan Solutions Portfolio</h2>
              <p className="text-muted-foreground mt-3 text-sm">A wide range of products spanning MSME, project, agriculture, women-led, and export finance — backed by Banks, NBFCs and private institutional funding.</p>
            </div>
              <a href={brochureUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-brandred hover:bg-brandred-700 text-white px-5 py-3 text-sm font-semibold rounded-sm shrink-0" data-testid="loan-brochure">
                <Download size={16} /> Download Brochure
              </a>
          </div>
          <LoanPortfolio prefix="loan" />
        </div>
      </section>

      {/* WHY US + IMAGE */}
      <section className="bg-navy text-white section-pad" data-testid="why-us">
        <div className="container-x">
            <span className="accent-bar mb-3" />
            <h2 className="font-heading text-3xl md:text-4xl font-bold">Why Choose Crew Connect FinInvesto?</h2>
            <p className="text-white/70 mt-3">Our advantage is speed, deep lender networks, and the willingness to take on cases others reject.</p>
          <div className="grid lg:grid-cols-2 gap-10 items-center mt-10">
            <div className="grid sm:grid-cols-2 gap-7">
              {WHY_US.map((w, i) => (
                <div key={w.title} className="border-l-2 border-brandred pl-4" data-testid={`why-${i}`}>
                  <div className="font-heading font-bold text-white">{w.title}</div>
                  <div className="text-sm text-white/70 mt-1">{w.desc}</div>
                </div>
              ))}
            </div>
            <figure className="financiers-photo" data-testid="why-us-photo">
              <img src={IMAGES.financiers} alt="Corporate financiers collaborating around a table and discussing financial documents" className="w-full h-full object-contain" loading="lazy" data-testid="why-us-image" />
            </figure>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad" data-testid="faq-section">
        <div className="container-x max-w-4xl">
          <span className="accent-bar mb-3" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy">Frequently Asked Questions</h2>
          <p className="text-muted-foreground mt-3">The most common questions from businesses and financiers we work with.</p>
          <div className="mt-10 space-y-3">
            {FAQ.map((f, i) => (
              <details key={i} className="faq-item group border border-border rounded-sm bg-white" data-testid={`faq-${i}`}>
                <summary className="flex items-start justify-between gap-4 p-5 md:p-6" data-testid={`faq-toggle-${i}`}>
                  <span className="font-heading font-semibold text-navy text-base md:text-lg">{f.q}</span>
                  <Plus size={22} className="faq-icon shrink-0 text-brandred mt-1" />
                </summary>
                <div className="px-5 md:px-6 pb-5 md:pb-6 -mt-1 text-sm md:text-[15px] text-navy/75 leading-relaxed">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section className="bg-secondary section-pad" data-testid="home-lead-section">
        <div className="container-x grid md:grid-cols-2 gap-12 items-start">
          <div>
            <span className="accent-bar mb-3" />
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy">Talk to a Funding Specialist</h2>
            <p className="text-muted-foreground mt-3">Drop your details — we'll route your case to the right lender pool. No upfront fees. No spam.</p>
            <div className="mt-8 space-y-4 text-sm text-navy">
              <Row icon={<Phone size={18} />} label="Call / WhatsApp" value={BRAND.phoneDisplay} />
              <Row icon={<Building2 size={18} />} label="Office" value={BRAND.address} />
              <Row icon={<TrendingUp size={18} />} label="Website" value={BRAND.website} />
            </div>
          </div>
          <LeadForm />
        </div>
      </section>
    </div>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-brandred mt-0.5">{icon}</span>
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="font-medium">{value}</div>
      </div>
    </div>
  );
}
